import React, { useEffect, useRef, useCallback } from 'react';

import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Navbar from './components/Navbar';
import Inicio from './pages/inicio';
import FaceCheckModal from './components/FaceRecognition/FaceCheckModal';
import { triggerCheck, clearFaceCheck, unlockSession } from './features/faceCheck/faceCheckSlice';
import { enableWebPush } from './push/registerPush';
import SubNavbar from './components/SubNavbar';
import KioskoInicio from './pages/KioskoInicio';
import OperacionInicio from './pages/OperacionInicio';
import HandheldInicio from './pages/HandheldInicio';
import HHBitacoraPage from './pages/HHBitacoraPage'
import HHDailyOperatorsInfo from './pages/HHDailyOperatorsInfo'
import HHLlegadas from './pages/HHllegadas'
import HHServicioManual from './pages/HHServicioManualPage'
import HHServicios from './pages/HHServicios'
import HHGestionT2 from './pages/HHGestionT2'
import PendientesPage from './pages/PendientesPage';
import COLlegadasPage from './pages/COLlegadasPage';
import COSalidasPage from './pages/COSalidasPage';
import GuardiaPage from './pages/GuardiaPage';
import GuardiasPage from './pages/GuardiasPage';
import GeoZonasPage from './pages/GeoZonasPage';
import GeoZonasView from './pages/GeoZonasView'
import ServiciosStandByPage from './pages/ServiciosStandByPage';
import ServiciosRegistradosPage from './pages/ServiciosRegistradosPage';
import ResumenVuelosPage from './pages/ResumenVuelosPage';
import ResumenVuelosPageDL from './pages/ResumenVuelosPageDL';
import ResumenVuelosPageUA from  './pages/ResumenVuelosPageUA';
import ResumenVuelosPageAA from  './pages/ResumenVuelosPageAA';
import ResumenVuelosPageIntegrado from './pages/ResumenVuelosPageIntegrado';
import BitacoraPage from './pages/BitacoraPage';
import GestionVuelos from './pages/GestionVuelos'
import FloatingBubbleModal from './components/FloatingBubbleModal';
import FloatingVuelosModal from './components/FloatingVuelosModal';
import FloatingGestionUsuariosModal from './components/FloatingGestionUsuariosModal';
import FloatingVuelosPasajeros from './components/FloatingVuelosPasajeros';
import SupportWidget from './components/SupportWidget';
import { useSelector, useDispatch } from 'react-redux';
import { setToken, clearToken } from './features/auth/authSlice';
import './components/FaceRecognition/FaceCheckModal.css';
import { SidebarProvider } from './context/SidebarContext';
import { useSidebar } from './context/SidebarContext';
import axios from 'axios';
import API_BASE_URL from './config';





// Función para decodificar el JWT
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// Función para validar el token
const isTokenValid = (token) => {
  if (!token) return false;
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) return false;
  return decoded.exp * 1000 >= Date.now();
};


/**
 * Componente invisible que renueva el token cada 30 min mientras el usuario
 * siga activo (clicks, teclas, movimiento). Si el usuario está inactivo más
 * de 60 min, se cierra la sesión.
 */
const TokenRefresher = () => {
  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const lastActivity = useRef(Date.now());
  const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 min
  const INACTIVITY_LIMIT = 60 * 60 * 1000; // 60 min

  const markActive = useCallback(() => {
    lastActivity.current = Date.now();
  }, []);

  useEffect(() => {
    window.addEventListener('click', markActive);
    window.addEventListener('keydown', markActive);
    window.addEventListener('mousemove', markActive);
    window.addEventListener('touchstart', markActive);
    return () => {
      window.removeEventListener('click', markActive);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('mousemove', markActive);
      window.removeEventListener('touchstart', markActive);
    };
  }, [markActive]);

  useEffect(() => {
    if (!token) return;

    const interval = setInterval(async () => {
      const inactivo = Date.now() - lastActivity.current;

      // Si el usuario lleva demasiado tiempo inactivo, cerrar sesión
      if (inactivo > INACTIVITY_LIMIT) {
        dispatch(clearToken());
        return;
      }

      // Renovar el token
      try {
        const res = await axios.post(
          `${API_BASE_URL}auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.token) {
          dispatch(setToken(res.data.token));
        }
      } catch (err) {
        console.error('Token refresh failed:', err.message);
        // Si el servidor rechaza el token, cerrar sesión
        if (err.response?.status === 401 || err.response?.status === 403) {
          dispatch(clearToken());
        }
      }
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [token, dispatch]);

  return null;
};

/**
 * Monitorea los tiempos programados y dispara el modal cuando corresponde.
 * Corre solo cuando el usuario es un coordinador autenticado.
 */
const FaceCheckScheduler = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { scheduledChecks, completedChecks, activeCheckIndex } = useSelector((state) => state.faceCheck);

  useEffect(() => {
    if (!token || scheduledChecks.length === 0) return;

    // Verificar cada 30 segundos si es hora de un check
    const interval = setInterval(() => {
      const now = Date.now();
      scheduledChecks.forEach((checkTime, index) => {
        const alreadyDone = completedChecks.includes(index);
        const alreadyActive = activeCheckIndex === index;
        if (!alreadyDone && !alreadyActive && now >= checkTime) {
          dispatch(triggerCheck(index));
        }
      });
    }, 30_000);

    return () => clearInterval(interval);
  }, [token, scheduledChecks, completedChecks, activeCheckIndex, dispatch]);

  return null;
};

/**
 * Pantalla de sesión bloqueada cuando el coordinador falla una verificación.
 * Permite reiniciar sesión — el turno continúa desde el primer check completado.
 */
const LockedScreen = () => {
  const dispatch = useDispatch();

  const handleRelogin = () => {
    dispatch(clearToken());      // limpiar token — irá al login
    dispatch(unlockSession());   // desbloquear para que el próximo login funcione
  };

  return (
    <div className="face-locked-screen">
      <div className="face-locked-icon">🔒</div>
      <h2 className="face-locked-title">Sesión bloqueada</h2>
      <p className="face-locked-sub">
        No se pudo verificar tu presencia. El incidente fue registrado.
        Vuelve a iniciar sesión para continuar con la operación.
      </p>
      <button className="face-locked-btn" onClick={handleRelogin}>
        Volver a iniciar sesión
      </button>
    </div>
  );
};

const App = () => {
  const token = useSelector((state) => state.auth.token);
  const isAuthenticated = isTokenValid(token);

  const RedirectByRole = ({ token }) => {
  const decoded = parseJwt(token);
  const role = decoded?.rol;

  if (role === 'colaborador') {
    return <Navigate to="/coordinacion" replace />;
  }

  if (role === 'operador') {
    return <Navigate to="/handheld" replace />;
  }

  return <Navigate to="/" replace />;
};



const AuthenticatedLayout = ({ children }) => {
  const location = useLocation();
  const { isCollapsed } = useSidebar(); // 🔥 Aquí ya tienes el estado del sidebar

  const isLogin = location.pathname === '/';
  const isHandheld = location.pathname.startsWith('/handheld');
  const isOperacion = location.pathname.startsWith('/operacion');
  const isKiosko = location.pathname.startsWith('/kiosko');
  const isCoordinacion = location.pathname.startsWith('/coordinacion');


  return (
    <>
      {!isLogin && <Navbar />}
      {!isLogin && (
  <div className="layout-body">
    <SubNavbar />
    <div className={`main-content ${isCollapsed ? 'collapsed' : ''}`}>
      {children}
    </div>
  </div>
)}


{isCoordinacion && (
  <>
    <FloatingBubbleModal />
  </>
)}

      {isHandheld && (
        <>
         <FloatingVuelosModal/>
        </>
      )}

      {!isLogin && <SupportWidget />}
    </>
  );
};

  const { locked } = useSelector((state) => state.faceCheck);
  const decoded = isAuthenticated ? parseJwt(token) : null;
  const isCoordinador = decoded?.rol === 'colaborador';

  // Alta de Web Push para el navegador del portal (notifica cuando llega un
  // servicio AM a la estación del usuario). No bloquea el render ni el login.
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const estacion = decoded?.estacion || null;
    enableWebPush({ token, estacion }).catch(() => { /* silencioso */ });
  }, [isAuthenticated, token, decoded?.estacion]);

  // Pantalla de bloqueo tiene prioridad total
  if (locked) return <LockedScreen />;

  return (
    <SidebarProvider>
    <Router>
      <TokenRefresher />
      {/* Checks faciales aleatorios — solo para coordinadores */}
      {isAuthenticated && isCoordinador && (
        <>
          <FaceCheckScheduler />
          <FaceCheckModal />
        </>
      )}
      <Routes>
        {/* Ruta para el Login */}
        <Route path="/" element={<Login />} />


        {/* Ruta para Servicios */}
       <Route
  path="/inicio"
  element={
    isAuthenticated ? (
      <RedirectByRole token={token} />
    ) : (
      <Navigate to="/" />
    )
  }
/>

        <Route
  path="/kiosko"
  element={isAuthenticated ? <AuthenticatedLayout><KioskoInicio /></AuthenticatedLayout> : <Navigate to="/" />}
/>
<Route
  path="/coordinacion"
  element={isAuthenticated ? <AuthenticatedLayout><OperacionInicio /></AuthenticatedLayout> : <Navigate to="/" />}
/>
<Route
  path="/handheld"
  element={isAuthenticated ? <AuthenticatedLayout><HandheldInicio /></AuthenticatedLayout> : <Navigate to="/" />}
/>

        {/* Ruta para Llegadas */}
        <Route
          path="/handheld/llegadas"
          element={
            isAuthenticated ?
             (
              <AuthenticatedLayout>
                <HHLlegadas />
              </AuthenticatedLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        /> 
        <Route
  path="/handheld/bitacora"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <HHBitacoraPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/handheld/salidas"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <HHServicios />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/handheld/gestion"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <HHDailyOperatorsInfo />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/gestiont2"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <GestionVuelos />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/geozonas"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <GeoZonasPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/geozonasview"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <GeoZonasView />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/handheld/manual"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <HHServicioManual />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/handheld/pendientes"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <PendientesPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/llegadaspage"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <COLlegadasPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/salidaspage"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <COSalidasPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/stdby"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <ServiciosStandByPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/resumenVuelos"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <ResumenVuelosPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/resumenVuelosDL"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <ResumenVuelosPageDL />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>
<Route
  path="/coordinacion/resumenVuelosAA"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <ResumenVuelosPageAA />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>
<Route
  path="/coordinacion/resumenVuelosUA"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <ResumenVuelosPageUA />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/resumenVuelosIntegrado"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <ResumenVuelosPageIntegrado />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>



<Route
  path="/coordinacion/registrados"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <ServiciosRegistradosPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>
<Route
  path="/coordinacion/rol"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <BitacoraPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/kiosko/guardia"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <GuardiaPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>

<Route
  path="/coordinacion/guardias"
  element={
    isAuthenticated ? (
      <AuthenticatedLayout>
        <GuardiasPage />
      </AuthenticatedLayout>
    ) : (
      <Navigate to="/" />
    )
  }
/>


        {/* Ruta para Llegadas 
        <Route
          path="/manual"
          element={
            isAuthenticated ?
             (
              <AuthenticatedLayout>
                <ServicioManual />
              </AuthenticatedLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        /> */}

        {/* Ruta para la Bitácora 
        <Route
          path="/bitacora"
          element={
            isAuthenticated ?
             (
              <AuthenticatedLayout>
                <BitacoraPage />
              </AuthenticatedLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        /> */}

        {/* Ruta para Gestión 
        <Route
          path="/gestion"
          element={
            isAuthenticated ?
             (
              <AuthenticatedLayout>
                <GestionPage />
              </AuthenticatedLayout>
            ) : (
              <Navigate to="/" />
            )
          }
        /> */}

        {/* Ruta por defecto o no encontrada */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
    </SidebarProvider>
  );
};

export default App;
